import { useEffect, useState } from "react";
import { readEffectiveTheme, subscribeEffectiveTheme } from "@weco/suite-chrome";

export type ThemeMode = "light" | "dark";

export const readThemeMode: () => ThemeMode = readEffectiveTheme;

/** The theme the suite is actually showing (the `.dark` class on <html>), not
 * the OS preference. A canvas/chart cannot re-render from a media query, so it
 * subscribes to the class instead. */
export function useThemeMode(): ThemeMode {
  const [mode, setMode] = useState<ThemeMode>(readEffectiveTheme);
  useEffect(() => {
    setMode(readEffectiveTheme());
    return subscribeEffectiveTheme(setMode);
  }, []);
  return mode;
}

/** The org brand colour as hex, read from CSS vars (no cascade in a canvas). */
export function brandHex(): string {
  if (typeof document === "undefined") return "#3366FF";
  const root = getComputedStyle(document.documentElement);
  const value =
    root.getPropertyValue("--color-primary").trim() ||
    root.getPropertyValue("--accent").trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : "#3366FF";
}

/** Status series colours for charts. Keyed loosely so this shared module
 * carries no office-domain type; the homework side re-exports it typed. */
export const STATUS_HEX: Record<string, string> = {
  available: "#10b981", focus: "#8b5cf6", meeting: "#f59e0b",
  break: "#0ea5e9", away: "#64748b", offline: "#94a3b8",
};
export const STATUS_HEX_DARK: Record<string, string> = {
  ...STATUS_HEX, away: "#94a3b8", offline: "#64748b",
};

/** Chart series colours (index 0 is the brand colour, added at runtime by
 * useChartColors — these are indices 1..n, which differ per theme). */
export const CHART_SERIES_DARK = ["#60a5fa", "#34d399", "#f472b6", "#fbbf24", "#a78bfa", "#22d3ee", "#fb7185"];
export const CHART_SERIES_LIGHT = ["#2563eb", "#059669", "#db2777", "#d97706", "#7c3aed", "#0891b2", "#e11d48"];

export type TintTone = "warning" | "danger" | "success" | "focus" | "neutral";

export const HW_TINT_INK: Record<TintTone, string> = {
  warning: "text-amber-700 dark:text-amber-300",
  danger: "text-rose-700 dark:text-rose-300",
  success: "text-emerald-700 dark:text-emerald-300",
  focus: "text-violet-700 dark:text-violet-300",
  neutral: "text-slate-600 dark:text-slate-300",
};
export const HW_TINT: Record<TintTone, string> = {
  warning: `bg-amber-500/10 ring-1 ring-inset ring-amber-500/25 ${HW_TINT_INK.warning}`,
  danger: `bg-rose-500/10 ring-1 ring-inset ring-rose-500/25 ${HW_TINT_INK.danger}`,
  success: `bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/25 ${HW_TINT_INK.success}`,
  focus: `bg-violet-500/10 ring-1 ring-inset ring-violet-500/25 ${HW_TINT_INK.focus}`,
  neutral: `bg-slate-500/10 ring-1 ring-inset ring-slate-500/25 ${HW_TINT_INK.neutral}`,
};
export const HW_TINT_BLOCK: Record<TintTone, string> = {
  warning: `bg-amber-500/10 border border-amber-500/25 ${HW_TINT_INK.warning}`,
  danger: `bg-rose-500/10 border border-rose-500/25 ${HW_TINT_INK.danger}`,
  success: `bg-emerald-500/10 border border-emerald-500/25 ${HW_TINT_INK.success}`,
  focus: `bg-violet-500/10 border border-violet-500/25 ${HW_TINT_INK.focus}`,
  neutral: `bg-slate-500/10 border border-slate-500/25 ${HW_TINT_INK.neutral}`,
};
